import type { ConversationMessage, SpineStep } from '../hooks/sessionTypes';
import type { NewModel } from './flowModel';
import { deriveCurrentStateVsmReadModel } from './currentStateVsmReadModel';
import { parseFlow } from './flowParser';

export type StreamMappingPartId =
  | 'story'
  | 'sequence'
  | 'current-state-vsm'
  | 'baseline-freeze';

export type StreamMappingPartStatus = 'to-do' | 'in-progress' | 'done';

export interface StreamMappingStreamLike {
  id: string;
  name: string;
  oneLiner?: string;
  smes?: Array<{ name?: string; contact?: string }>;
  mappingSessionId?: string;
  vsmBaseline?: unknown;
  sequenceOverlay?: unknown;
  painLinks?: unknown[];
}

export interface StreamMappingSessionLike {
  sessionId?: string | null;
  conversationHistory?: ConversationMessage[];
  newModel?: Partial<NewModel> | null;
  spine?: Array<Partial<SpineStep>>;
}

export interface StreamMappingPartDefinition {
  id: StreamMappingPartId;
  label: string;
  shortLabel: string;
  why: string;
  unlocks: string;
  workspacePart: StreamMappingPartId;
}

export interface StreamMappingPart extends StreamMappingPartDefinition {
  status: StreamMappingPartStatus;
  target: string;
  summary: string;
  actionLabel: string;
}

export interface StreamMappingProgress {
  parts: StreamMappingPart[];
  completedCount: number;
  totalCount: number;
  nextPart: StreamMappingPart | null;
}

export const STREAM_MAPPING_PARTS: StreamMappingPartDefinition[] = [
  {
    id: 'story',
    label: 'Story captured',
    shortLabel: 'Story',
    why: "The SME's plain-language account. It stays the trust anchor for everything that follows.",
    unlocks: 'Unlocks the business sequence.',
    workspacePart: 'story',
  },
  {
    id: 'sequence',
    label: 'Business sequence',
    shortLabel: 'Sequence',
    why: 'The story turned into ordered business steps you can correct.',
    unlocks: 'Unlocks the current-state measure.',
    workspacePart: 'sequence',
  },
  {
    id: 'current-state-vsm',
    label: 'Current-state VSM',
    shortLabel: 'Current-state VSM',
    why: 'Time, wait, and quality against each step. This is what makes value visible.',
    unlocks: 'Unlocks baseline freeze.',
    workspacePart: 'current-state-vsm',
  },
  {
    id: 'baseline-freeze',
    label: 'Baseline frozen',
    shortLabel: 'Freeze baseline',
    why: 'Locks the current state so the stream appears on the value-proven board.',
    unlocks: 'Unlocks the value-proven board.',
    workspacePart: 'baseline-freeze',
  },
];

const EMPTY_MODEL: NewModel = {
  flowText: '',
  openQuestions: '',
  nextQuestion: '',
  raw: '',
  frozenBoundary: 0,
};

export function streamMappingPartTarget(
  streamId: string,
  partId: StreamMappingPartId,
): string {
  return `/streams/${encodeURIComponent(streamId)}/workspace?part=${partId}`;
}

export function deriveStreamMappingProgress(input: {
  stream: StreamMappingStreamLike;
  session: StreamMappingSessionLike;
}): StreamMappingProgress {
  const { stream, session } = input;
  const newModel = { ...EMPTY_MODEL, ...(session.newModel ?? {}) };
  const flowText = typeof newModel.flowText === 'string' ? newModel.flowText : '';
  const parsedStatements = parseStatements(flowText);
  const spine = Array.isArray(session.spine) ? session.spine : [];
  const conversationHistory = Array.isArray(session.conversationHistory)
    ? session.conversationHistory
    : [];
  const mappingStarted = Boolean(stream.mappingSessionId || session.sessionId);
  const userNarrativeMessages = conversationHistory.filter(
    (message) => message.type === 'user' || message.type === 'transcript',
  );
  const openQuestionCount = countOpenQuestions(newModel.openQuestions);
  const statementCount = parsedStatements.length || spine.length;
  const hasStory = statementCount > 0 || userNarrativeMessages.length > 0;
  const sequenceStepCount = countSequenceSteps(stream.sequenceOverlay) || parsedStatements.length;
  const reviewNeededCount =
    spine.filter((step) => step.state === 'uncertain' || step.needsCheck).length
    + openQuestionCount;
  const hasSequence = sequenceStepCount > 0;

  const vsmReadModel = deriveCurrentStateVsmReadModel({
    stream,
    sessionId: session.sessionId ?? stream.mappingSessionId ?? null,
    flowText,
    vsm: newModel.vsm ?? null,
    spine,
  });
  const hasBaseline = Boolean(stream.vsmBaseline);
  const vsmStatus = deriveVsmPartStatus(vsmReadModel.status, hasBaseline, hasSequence);

  const storyStatus: StreamMappingPartStatus = hasStory
    ? 'done'
    : mappingStarted
      ? 'in-progress'
      : 'to-do';
  const sequenceStatus: StreamMappingPartStatus = hasSequence
    ? 'done'
    : hasStory
      ? 'in-progress'
      : 'to-do';
  const freezeStatus: StreamMappingPartStatus = hasBaseline
    ? 'done'
    : vsmStatus === 'done'
      ? 'in-progress'
      : 'to-do';

  const statusById: Record<StreamMappingPartId, StreamMappingPartStatus> = {
    story: storyStatus,
    sequence: sequenceStatus,
    'current-state-vsm': vsmStatus,
    'baseline-freeze': freezeStatus,
  };

  const parts = STREAM_MAPPING_PARTS.map((definition) => {
    const status = statusById[definition.id];
    return {
      ...definition,
      status,
      target: streamMappingPartTarget(stream.id, definition.workspacePart),
      summary: summaryForPart(definition.id, status, {
        mappingStarted,
        statementCount,
        userNarrativeCount: userNarrativeMessages.length,
        openQuestionCount,
        nextQuestion: newModel.nextQuestion,
        sequenceStepCount,
        reviewNeededCount,
        vsmFlowCount: vsmReadModel.flow.length,
        vsmMissingFactCount: vsmReadModel.missingFacts.length,
        hasBaseline,
        baseline: stream.vsmBaseline,
      }),
      actionLabel: actionLabelForPart(definition.id, status),
    };
  });

  const completedCount = parts.filter((part) => part.status === 'done').length;
  const nextPart = parts.find((part) => part.status !== 'done') ?? null;

  return {
    parts,
    completedCount,
    totalCount: parts.length,
    nextPart,
  };
}

function deriveVsmPartStatus(
  readStatus: 'empty' | 'partial' | 'ready' | 'frozen',
  hasBaseline: boolean,
  hasSequence: boolean,
): StreamMappingPartStatus {
  if (hasBaseline || readStatus === 'ready' || readStatus === 'frozen') return 'done';
  if (readStatus === 'partial') return 'in-progress';
  return hasSequence ? 'to-do' : 'to-do';
}

function summaryForPart(
  partId: StreamMappingPartId,
  status: StreamMappingPartStatus,
  facts: {
    mappingStarted: boolean;
    statementCount: number;
    userNarrativeCount: number;
    openQuestionCount: number;
    nextQuestion?: string;
    sequenceStepCount: number;
    reviewNeededCount: number;
    vsmFlowCount: number;
    vsmMissingFactCount: number;
    hasBaseline: boolean;
    baseline: unknown;
  },
): string {
  switch (partId) {
    case 'story':
      if (status === 'done') {
        const count = facts.statementCount || facts.userNarrativeCount;
        const base = count > 0 ? plural(count, 'statement', 'captured') : 'Story captured';
        return facts.openQuestionCount > 0
          ? `${base}. ${plural(facts.openQuestionCount, 'open question')}.`
          : `${base}.`;
      }
      if (status === 'in-progress') {
        return facts.nextQuestion && facts.nextQuestion.trim()
          ? `Latest prompt: ${truncate(facts.nextQuestion.trim(), 96)}`
          : 'Capture the SME story in chat.';
      }
      return 'Start with the SME story so the sequence has a trust anchor.';
    case 'sequence':
      if (status === 'done') {
        const review = facts.reviewNeededCount > 0
          ? `. ${facts.reviewNeededCount} still need a look.`
          : '.';
        return `${plural(facts.sequenceStepCount, 'step', 'agreed')}${review}`;
      }
      if (status === 'in-progress') return 'Turn the story into ordered business steps.';
      return 'Opens once the story starts to form.';
    case 'current-state-vsm':
      if (status === 'done') {
        return facts.vsmFlowCount > 0
          ? `${plural(facts.vsmFlowCount, 'material step', 'measured')}.`
          : 'Current-state readout ready.';
      }
      if (status === 'in-progress') {
        return facts.vsmMissingFactCount > 0
          ? `${plural(facts.vsmMissingFactCount, 'missing fact')} to resolve.`
          : 'Capture time, wait, and quality against the ordered steps.';
      }
      return 'Opens once the business sequence is in place.';
    case 'baseline-freeze':
      if (status === 'done') return baselineSummary(facts.baseline);
      if (status === 'in-progress') return 'Ready to freeze once the room agrees the baseline.';
      return 'Opens once the measure is good enough to review.';
    default:
      return '';
  }
}

function actionLabelForPart(
  partId: StreamMappingPartId,
  status: StreamMappingPartStatus,
): string {
  if (status === 'in-progress') {
    if (partId === 'baseline-freeze') return 'Freeze baseline';
    return 'Continue';
  }
  switch (partId) {
    case 'story':
      return 'Start with the story';
    case 'sequence':
      return 'Start sequence';
    case 'current-state-vsm':
      return 'Start current-state VSM';
    case 'baseline-freeze':
      return 'Freeze baseline';
    default:
      return 'Continue';
  }
}

function parseStatements(flowText: string): Array<{ id: number; name: string }> {
  if (!flowText.trim()) return [];
  try {
    const statements = parseFlow(flowText).statements.map((statement) => ({
      id: statement.id,
      name: statement.name,
    }));
    if (statements.length > 0) return statements;
  } catch {
    // Fall through to the line-count fallback below.
  }
  return flowText
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, index) => ({ id: index + 1, name: line }));
}

function countSequenceSteps(raw: unknown): number {
  if (Array.isArray(raw)) return raw.length;
  if (!raw || typeof raw !== 'object') return 0;
  const record = raw as Record<string, unknown>;
  for (const key of ['steps', 'sequence', 'items', 'orderedSteps']) {
    const value = record[key];
    if (Array.isArray(value)) return value.length;
  }
  return 0;
}

function countOpenQuestions(raw: unknown): number {
  if (typeof raw !== 'string' || !raw.trim()) return 0;
  return raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean).length;
}

function baselineSummary(raw: unknown): string {
  if (!raw || typeof raw !== 'object') return 'Baseline frozen. Ready for the value board.';
  const record = raw as Record<string, unknown>;
  const dateValue = readString(record, ['frozenAt', 'createdAt', 'capturedAt', 'updatedAt']);
  const status = readString(record, ['status']);
  if (dateValue) return `Baseline frozen ${formatDate(dateValue)}.`;
  if (status) return `Baseline frozen. ${status}.`;
  return 'Baseline frozen. Ready for the value board.';
}

function readString(record: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

function formatDate(raw: string): string {
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return `on ${date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })}`;
}

function plural(count: number, noun: string, verb?: string): string {
  const phrase = `${count} ${noun}${count === 1 ? '' : 's'}`;
  return verb ? `${phrase} ${verb}` : phrase;
}

function truncate(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max - 1).trimEnd()}...` : value;
}
